export interface JobSubOptionFollowUp {
  type: 'text'
  label: string
  placeholder: string
  required?: boolean
}

export interface JobSubOption {
  id: string
  label: string
  emoji: string
  followUp?: JobSubOptionFollowUp
}

export interface JobCategory {
  id: string
  label: string
  emoji: string
  subQuestion?: string
  /** If true, the user may select more than one sub-option */
  multiSelect?: boolean
  subOptions?: JobSubOption[]
}

export const JOB_CATEGORIES: JobCategory[] = [
  {
    id: 'pipe_leak',
    label: 'Leak or burst pipe',
    emoji: '💧',
    subQuestion: 'How bad is it?',
    subOptions: [
      { id: 'flooding',  label: 'Flooding / water everywhere', emoji: '🌊' },
      { id: 'flowing',   label: 'Water actively flowing',      emoji: '💧' },
      { id: 'dripping',  label: 'Steady drip or small leak',   emoji: '💦' },
      { id: 'damp',      label: 'Damp patch / not sure where', emoji: '🏠' },
    ],
  },
  {
    id: 'boiler_heating',
    label: 'Boiler / heating',
    emoji: '🌡️',
    subQuestion: "What's happening? (select all that apply)",
    multiSelect: true,
    subOptions: [
      { id: 'no_heating',   label: 'No heating / radiators cold',  emoji: '❄️' },
      { id: 'no_hot_water', label: 'No hot water',                 emoji: '🚿' },
      { id: 'wont_start',   label: "Boiler won't turn on",         emoji: '❌' },
      { id: 'keeps_off',    label: 'Boiler keeps switching off',   emoji: '🔄' },
      {
        id: 'error_code',
        label: 'Error code / warning light',
        emoji: '⚠️',
        followUp: {
          type: 'text',
          label: 'What error code is showing?',
          placeholder: 'e.g. F29, E119, EA',
          required: false,
        },
      },
      { id: 'noisy',      label: 'Making unusual noises', emoji: '🔊' },
      { id: 'pilot_out',  label: 'Pilot light out',       emoji: '🔥' },
    ],
  },
  {
    id: 'blocked_drain',
    label: 'Blocked drain',
    emoji: '🕳️',
    subQuestion: 'Which drain? (select all that apply)',
    multiSelect: true,
    subOptions: [
      { id: 'kitchen',  label: 'Kitchen sink',         emoji: '🍳' },
      { id: 'bathroom', label: 'Bath or shower',       emoji: '🛁' },
      { id: 'toilet',   label: 'Toilet (overflowing)', emoji: '🚽' },
      { id: 'multiple', label: 'Multiple drains',      emoji: '🌊' },
      { id: 'outside',  label: 'Outside / manhole',    emoji: '🏠' },
    ],
  },
  {
    id: 'blocked_toilet',
    label: 'Blocked toilet',
    emoji: '🚽',
    subQuestion: "What's happening?",
    subOptions: [
      { id: 'wont_flush',  label: "Won't flush properly",    emoji: '🔄' },
      { id: 'overflowing', label: 'Overflowing / backing up', emoji: '🌊' },
      { id: 'base_leak',   label: 'Leaking at the base',      emoji: '💧' },
    ],
  },
  {
    id: 'frozen_pipe',
    label: 'Frozen pipe',
    emoji: '🧊',
    subQuestion: 'Any visible damage?',
    subOptions: [
      { id: 'burst',     label: 'Pipe has burst or cracked',  emoji: '💥' },
      { id: 'suspected', label: 'No flow, no visible damage', emoji: '❓' },
    ],
  },
  {
    id: 'water_pressure',
    label: 'Low pressure',
    emoji: '📉',
    subQuestion: 'Which taps are affected?',
    subOptions: [
      { id: 'all',       label: 'All taps / whole property', emoji: '🏠' },
      { id: 'cold_only', label: 'Cold water only',           emoji: '❄️' },
      { id: 'hot_only',  label: 'Hot water only',            emoji: '🔥' },
      { id: 'one_tap',   label: 'Just one tap',              emoji: '🔧' },
    ],
  },
  {
    id: 'radiator',
    label: 'Radiator issue',
    emoji: '🔥',
    subQuestion: "What's the problem? (select all that apply)",
    multiSelect: true,
    subOptions: [
      { id: 'leaking',     label: 'Leaking',                  emoji: '💧' },
      { id: 'not_heating', label: 'Not heating up',           emoji: '❄️' },
      { id: 'noisy',       label: 'Noisy (banging/gurgling)', emoji: '🔊' },
    ],
  },
  {
    id: 'tap_repair',
    label: 'Tap / fixture',
    emoji: '🔧',
    subQuestion: 'Which fixture?',
    subOptions: [
      { id: 'dripping_tap',   label: 'Dripping tap',            emoji: '💧' },
      { id: 'broken_tap',     label: 'Broken / seized tap',     emoji: '❌' },
      { id: 'running_toilet', label: 'Running / broken toilet', emoji: '🚽' },
      { id: 'shower',         label: 'Shower issue',            emoji: '🚿' },
    ],
  },
  {
    id: 'other',
    label: 'Other issue',
    emoji: '🛠️',
  },
]

/**
 * Turns a category + sub-selections + follow-ups into the one-line
 * issueDescription a real plumber sees - the single source of truth for that
 * phrasing, shared by EmergencyForm.tsx (the site's own booking form) and
 * ChatPanel.tsx's CategoryPickerCard (issue #37's chat equivalent), so a
 * booking reads identically regardless of which path the customer took.
 * Previously two near-identical copies of this logic existed and could
 * silently drift apart.
 */
export function buildIssueDescription(params: {
  categoryId: string
  subSelections: string[]
  followUpValues: Record<string, string>
  /** Freeform text for the 'other' category. */
  otherText: string
  /** Extra trailing note, appended after a full stop - EmergencyForm's own
   *  "Add extra info" step has one; the chat has no equivalent step yet. */
  additionalInfo?: string
}): string {
  const { categoryId, subSelections, followUpValues, otherText, additionalInfo = '' } = params
  const extra = additionalInfo.trim()
  const withExtra = (base: string) => (extra ? `${base}. ${extra}` : base)

  const cat = JOB_CATEGORIES.find(c => c.id === categoryId)
  if (!cat || cat.id === 'other') {
    const desc = otherText.trim()
    return withExtra(desc ? `Other issue - ${desc}` : 'Other plumbing issue')
  }

  if (!cat.subOptions?.length || subSelections.length === 0) return withExtra(cat.label)

  const subLabels = subSelections
    .map(id => {
      const opt = cat.subOptions!.find(o => o.id === id)
      if (!opt) return null
      const fu = followUpValues[id]?.trim()
      return fu ? `${opt.label} (${fu})` : opt.label
    })
    .filter(Boolean)
    .join(', ')
  return withExtra(`${cat.label} - ${subLabels}`)
}
